import { faArrowLeft } from "@jewlr/pro-light-svg-icons/faArrowLeft"
import { faArrowRight } from "@jewlr/pro-light-svg-icons/faArrowRight"
import { faChevronDown } from "@jewlr/pro-light-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-light-svg-icons/faChevronUp"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { forcePanelIndex } from "areas/product/store/actions"
import Button from "components/ui/button"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { isTestMode } from "helpers/application"

import Heading from "./heading"
import Step from "./step"

const getSelectedStoneCategory = (currentPref, wizardData, selectedChoice) => {
  if (currentPref === "as") return null

  return selectedChoice
    ? wizardData.categories.find((category) =>
        category.options.some((option) => option.value === selectedChoice.value)
      )
    : wizardData.categories[0]
}

const GuidedStonePickerWizard = ({ prefs }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [openTooltip, setOpenTooltip] = useState(false)
  const dispatch = useDispatch()

  const totalSteps = prefs.length
  const isFirstStep = currentStep === 1
  const isLastStep = currentStep === totalSteps
  const currentPrefIndex = currentStep - 1
  const currentPref = prefs[currentPrefIndex]

  const wizardData = useSelector((state) => state.wizard.data[currentPref])
  const selectedChoice = useSelector((state) => state.choices[currentPref])
  const styleCode = useSelector((state) => state.choices.style_code?.value)

  const [selectedCategory, setSelectedCategory] = useState(() =>
    getSelectedStoneCategory(currentPref, wizardData, selectedChoice)
  )

  const handleGoToPref = useCallback(
    (pref) => {
      const prefIndex = prefs.indexOf(pref)
      if (prefIndex !== -1) {
        if (!isTestMode) {
          dataLayer.push({
            event: "opened-stone-selector",
            optionName: pref,
            styleCode,
          })
        }

        dispatch(forcePanelIndex("stone-selector"))
        setCurrentStep(prefIndex + 1)
      }
    },
    [dispatch, prefs, styleCode]
  )

  const handleCategoryClick = useCallback(
    (cat) => {
      if (!cat) return

      if (!isTestMode) {
        dataLayer.push({
          event: "clicked-stone-category",
          optionName: cat.type,
          styleCode,
        })
      }

      setSelectedCategory(cat)
    },
    [styleCode]
  )

  const handleTooltipClick = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-tool-tip",
        optionName: selectedCategory?.title,
        styleCode,
      })
    }

    setOpenTooltip((prev) => !prev)
  }, [selectedCategory, styleCode])

  const prevStepTitle = useSelector((state) =>
    isFirstStep
      ? null
      : state.wizard.data?.[prefs[currentPrefIndex - 1]]?.heading?.title
  )

  const nextStepTitle = useSelector((state) =>
    isLastStep
      ? null
      : state.wizard.data?.[prefs[currentPrefIndex + 1]]?.heading?.title
  )

  const nextStep = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-guide-next-step",
      })
    }

    setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
  }, [totalSteps])

  const prevStep = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-guide-prev-step",
      })
    }

    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }, [])

  const isOpened = useSelector((state) =>
    state.panel.openedPanelIndexes.includes("stone-selector")
  )

  useEffect(() => {
    const category = getSelectedStoneCategory(
      currentPref,
      wizardData,
      selectedChoice
    )
    if (category) {
      setSelectedCategory(category)
    }
  }, [currentPref, wizardData, selectedChoice])

  return (
    <div
      className="border-b-grey-10 border-b"
      data-cy="wizard-accordion-stone-selector"
    >
      <Heading
        currentPref={currentPref}
        handleGoToPref={handleGoToPref}
        prefs={prefs}
      />
      <Expand
        aria-labelledby="wizard-accordion-heading-stone-selector"
        className="my-8"
        id="wizard-accordion-content-stone-selector"
        role="region"
        show={isOpened}
      >
        <Step
          handleCategoryClick={handleCategoryClick}
          pref={currentPref}
          selectedCategory={selectedCategory}
        />
        <div className="flex items-center justify-center gap-24">
          {!isFirstStep && (
            <Button
              aria-label={`Go back to ${prevStepTitle}`}
              className="text-14 text-black-print gap-10"
              onClick={prevStep}
              size="md"
              underline
              variant="tertiary"
            >
              <FontAwesomeIcon className="text-12" icon={faArrowLeft} />
              {prevStepTitle}
            </Button>
          )}
          {!isLastStep && (
            <Button
              aria-label={`Go to ${nextStepTitle}`}
              className="text-14 text-black-print gap-10"
              onClick={nextStep}
              size="md"
              underline
              variant="tertiary"
            >
              {nextStepTitle}
              <FontAwesomeIcon className="text-12" icon={faArrowRight} />
            </Button>
          )}
        </div>
        {currentPref !== "as" && selectedCategory?.help_text && (
          <>
            <h4>
              <Button
                aria-controls={`${currentPref}-${selectedCategory.title}-tooltip`}
                className="text-12 w-full justify-between pt-8 font-semibold capitalize"
                id={`${currentPref}-${selectedCategory.title}-heading`}
                onClick={handleTooltipClick}
                text
                underline
                variant="secondary"
              >
                <span>What Are {selectedCategory.title}?</span>
                <FontAwesomeIcon
                  className="text-12"
                  icon={openTooltip ? faChevronUp : faChevronDown}
                />
              </Button>
            </h4>
            <Expand
              aria-labelledby={`${currentPref}-${selectedCategory.title}-heading`}
              id={`${currentPref}-${selectedCategory.title}-tooltip`}
              show={openTooltip}
            >
              <p className="text-12 text-grey-400 w-4/5 pt-8 font-semibold">
                {selectedCategory.help_text}
              </p>
            </Expand>
          </>
        )}
      </Expand>
    </div>
  )
}

GuidedStonePickerWizard.propTypes = {
  prefs: PropTypes.array.isRequired,
}

export default GuidedStonePickerWizard
